import { Fragment } from 'react';
import { Heading, Paragraph } from '@zillow/constellation-10';
import { compact, isEmpty, take } from 'lodash-es';
import { getApplicantName } from '@zg-rentals/applications-utils';
import type { AdditionalOccupant, Answers } from '../../../../../models/rental-application';
import { getIsRenterOccupant, getOccupantCount } from '../../../../../utils/additionalOccupants';
import { formatDate } from '../../../../../utils/formatters';

type Props = {
  answers: Answers;
};

type AdditionalOccupantDisplay = {
  title: string;
  value: string;
};

function getAdditionalOccupantDisplay(
  additionalOccupant: AdditionalOccupant | null | undefined,
  index: number,
  renterFullName: string,
): AdditionalOccupantDisplay | null {
  const isRenterOccupant = getIsRenterOccupant(index);
  const fullName = isRenterOccupant ? null : additionalOccupant?.fullName?.trim();
  const dateOfBirth = additionalOccupant?.dateOfBirth ? formatDate(additionalOccupant.dateOfBirth) : null;
  if (!fullName && !dateOfBirth) {
    return null;
  }
  return {
    title: isRenterOccupant ? `Occupant #${index + 1} (you)` : `Occupant #${index + 1}`,
    value: compact([isRenterOccupant ? renterFullName : fullName, dateOfBirth]).join(' · '),
  };
}

export function AdditionalOccupantsOverview({ answers }: Props) {
  const renterFullName = getApplicantName(answers);
  const additionalOccupantDisplays = compact(
    take(answers.additionalOccupants, getOccupantCount(answers.occupants)).map((additionalOccupant, index) =>
      getAdditionalOccupantDisplay(additionalOccupant, index, renterFullName),
    ),
  );

  if (isEmpty(additionalOccupantDisplays)) {
    return null;
  }

  return (
    <>
      {additionalOccupantDisplays.map(({ title, value }) => (
        <Fragment key={title}>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            {title}
          </Heading>
          <Paragraph>{value}</Paragraph>
        </Fragment>
      ))}
    </>
  );
}
